import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          <div className="prose max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                Introduction
              </h2>
              <p className="text-gray-700">
                DocMarket ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and share your personal information when you use our academic marketplace platform. By using DocMarket, you consent to the data practices described in this policy.
              </p>
              <div className="bg-blue-50 border border-blue-300 rounded p-3 mt-3">
                <p className="text-blue-900 text-sm">
                  <strong>Compliance:</strong> This Privacy Policy complies with United States privacy laws, including the California Consumer Privacy Act (CCPA), Children's Online Privacy Protection Act (COPPA), and other applicable federal and state regulations.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                1. Information We Collect
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.1 Account Registration Information</h3>
              <p className="text-gray-700 mb-2">When you create an account, we collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li><strong>Name:</strong> Your full name for account identification</li>
                <li><strong>Email Address:</strong> Used for account verification, login, and communications</li>
                <li><strong>School/Institution:</strong> The name of the school you attend or have attended</li>
                <li><strong>Field of Study:</strong> Your academic field or major (optional)</li>
                <li><strong>Password:</strong> Encrypted and securely stored for account access</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.2 Profile Information</h3>
              <p className="text-gray-700 mb-2">You may optionally provide:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Profile biography</li>
                <li>Profile picture or avatar</li>
                <li>Additional academic background details</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.3 Content and Files</h3>
              <p className="text-gray-700 mb-2">When you use the Platform, we collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li><strong>Uploaded Documents:</strong> PDFs, images, files you upload for sale</li>
                <li><strong>Messages:</strong> Direct messages sent through our platform</li>
                <li><strong>Ratings and Reviews:</strong> Feedback you provide on purchased documents</li>
                <li><strong>Comments and Interactions:</strong> Any public comments or engagement</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                By uploading content to DocMarket, you consent to share that information with the site and its users in accordance with our Terms of Use.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.4 Payment Information</h3>
              <p className="text-gray-700 mb-3">
                We use Stripe as our payment processor. Payment card information (card number, CVV, expiration date) is collected and processed directly by Stripe. <strong>We do not store your payment card details on our servers.</strong>
              </p>
              <p className="text-gray-700 mb-2">We do collect and store:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Transaction history (purchases and sales)</li>
                <li>Payment amounts and dates</li>
                <li>Stripe transaction IDs for record-keeping</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">1.5 Automatically Collected Information</h3>
              <p className="text-gray-700 mb-2">When you use the Platform, we automatically collect:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Usage Data:</strong> Pages viewed, features used, time spent on the platform</li>
                <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                <li><strong>IP Address:</strong> For security and fraud prevention</li>
                <li><strong>Cookies:</strong> Small data files stored on your device (see Section 8)</li>
                <li><strong>Log Data:</strong> Error logs, access times, and system events</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                2. How We Use Your Information
              </h2>
              <p className="text-gray-700 mb-2">We use collected information for the following purposes:</p>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.1 Platform Operations</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Create and manage your account</li>
                <li>Process transactions and payments</li>
                <li>Enable document uploads, downloads, and sharing</li>
                <li>Facilitate messaging between users</li>
                <li>Display your profile and uploaded content to other users</li>
                <li>Provide customer support and respond to inquiries</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.2 Communications</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Send transactional emails (purchase confirmations, account updates)</li>
                <li>Deliver notifications about platform activity</li>
                <li>Respond to support requests and questions</li>
                <li>Send important security or policy updates</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.3 Platform Improvement</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Analyze usage patterns to improve features</li>
                <li>Conduct research and development</li>
                <li>Fix bugs and optimize performance</li>
                <li>Develop new features and services</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">2.4 Safety and Security</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Detect and prevent fraud, spam, and abuse</li>
                <li>Enforce our Terms of Use and policies</li>
                <li>Protect against copyright infringement</li>
                <li>Investigate security incidents</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                3. How We Store Your Information
              </h2>
              <p className="text-gray-700 mb-3">
                Your information is securely stored in our database infrastructure. We implement industry-standard security measures including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li><strong>Encryption:</strong> Data is encrypted in transit (HTTPS/TLS) and at rest</li>
                <li><strong>Access Controls:</strong> Strict authentication and authorization protocols</li>
                <li><strong>Password Security:</strong> Passwords are hashed using bcrypt encryption</li>
                <li><strong>Regular Backups:</strong> Data is backed up to prevent loss</li>
                <li><strong>Monitoring:</strong> Continuous security monitoring and logging</li>
              </ul>
              <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-3">
                <p className="text-yellow-900 text-sm">
                  <strong>Note:</strong> While we implement robust security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                4. Information Sharing and Disclosure
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.1 With Other Users</h3>
              <p className="text-gray-700 mb-2">The following information is visible to other platform users:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Your name and profile information</li>
                <li>Documents you upload for sale</li>
                <li>Ratings and reviews you write</li>
                <li>Public profile details (school, field of study)</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.2 With Third-Party Service Providers</h3>
              <div className="bg-blue-50 border border-blue-300 rounded p-3 mb-3">
                <p className="text-blue-900 font-semibold">Stripe Payment Processing</p>
                <p className="text-blue-800 text-sm mt-1">
                  We share transaction data with Stripe to process payments. This includes buyer information, payment amounts, and transaction details. Stripe processes this information according to their Privacy Policy.
                </p>
              </div>
              <p className="text-gray-700 mb-3">
                <strong>We ONLY share data with Stripe for payment processing purposes. We do not sell or share your personal information with any other third parties for marketing purposes.</strong>
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.3 Legal Requirements</h3>
              <p className="text-gray-700 mb-2">We may disclose your information when required by law or to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Comply with legal processes (subpoenas, court orders)</li>
                <li>Enforce our Terms of Use and policies</li>
                <li>Protect our rights, property, or safety</li>
                <li>Investigate fraud or security issues</li>
                <li>Cooperate with law enforcement</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.4 Business Transfers</h3>
              <p className="text-gray-700">
                If DocMarket is involved in a merger, acquisition, or sale of assets, your information may be transferred. We will notify you via email and/or platform notice before your information becomes subject to a different privacy policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                5. Data Retention
              </h2>
              <p className="text-gray-700 mb-2">
                We retain your information for as long as necessary to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Maintain your account and provide services</li>
                <li>Comply with legal obligations (tax records, transaction history)</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Support business operations and analytics</li>
              </ul>
              <p className="text-gray-700">
                After account deletion, we may retain certain information for legal, security, and fraud prevention purposes for up to 7 years as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                6. Your Rights and Choices
              </h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.1 Access and Update</h3>
              <p className="text-gray-700 mb-2">You have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Access your personal information at any time through your account</li>
                <li>Update or correct inaccurate information</li>
                <li>Modify your profile details</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.2 Data Deletion and Account Removal</h3>
              <div className="bg-green-50 border border-green-300 rounded p-3 mb-3">
                <p className="text-green-900 font-semibold">You have the right to request deletion of your data and account.</p>
              </div>
              <p className="text-gray-700 mb-2">To delete your account and data:</p>
              <ol className="list-decimal pl-6 text-gray-700 space-y-2 mb-3">
                <li>Contact us at privacy@docmarket.com with your account email</li>
                <li>We will verify your identity</li>
                <li>Your account will be deleted within 30 days</li>
                <li>Some information may be retained for legal compliance</li>
              </ol>
              <p className="text-gray-700 text-sm italic">
                Note: Deleting your account will remove your profile, uploaded documents (unless purchased by others), and personal information. Transaction records may be retained for tax and legal purposes.
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.3 California Privacy Rights (CCPA)</h3>
              <p className="text-gray-700 mb-2">If you are a California resident, you have the right to:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li><strong>Know:</strong> Request information about data we collect, use, and share</li>
                <li><strong>Delete:</strong> Request deletion of your personal information</li>
                <li><strong>Opt-Out:</strong> Opt out of the sale of your personal information (we do not sell data)</li>
                <li><strong>Non-Discrimination:</strong> Not be discriminated against for exercising your rights</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, contact us at privacy@docmarket.com
              </p>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">6.4 Marketing Communications</h3>
              <p className="text-gray-700">
                You may opt out of marketing emails by clicking "unsubscribe" in any promotional email. You cannot opt out of transactional emails (purchase confirmations, account notifications) necessary for platform operation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                7. Children's Privacy (COPPA Compliance)
              </h2>
              <div className="bg-red-50 border border-red-300 rounded p-3 mb-3">
                <p className="text-red-900 font-semibold">Age Requirement: 13 Years or Older</p>
              </div>
              <p className="text-gray-700 mb-3">
                DocMarket requires users to be at least 13 years of age. We do not knowingly collect personal information from children under 13. The Platform is designed for students in higher education (college and above).
              </p>
              <p className="text-gray-700 mb-2">
                If we discover that a user is under 13:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>We will immediately delete their account and information</li>
                <li>We will not process any payments or transactions</li>
                <li>We will notify the registered email address</li>
              </ul>
              <p className="text-gray-700">
                Parents or guardians who believe their child has provided information to us should contact privacy@docmarket.com immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                8. Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-700 mb-3">
                We use cookies and similar technologies to enhance your experience on the Platform.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.1 Types of Cookies We Use</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li><strong>Essential Cookies:</strong> Required for login and account authentication</li>
                <li><strong>Functionality Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how you use the platform</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.2 Managing Cookies</h3>
              <p className="text-gray-700">
                You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                9. International Users
              </h2>
              <p className="text-gray-700">
                DocMarket operates in the United States. By using the Platform, you consent to the transfer and processing of your information in the United States. U.S. data protection laws may differ from those in your country.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                10. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 mb-3">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-3">
                <li>Post the updated policy on this page</li>
                <li>Update the "Last Updated" date</li>
                <li>Notify you via email for material changes</li>
                <li>Provide notice through the Platform</li>
              </ul>
              <p className="text-gray-700">
                Your continued use of DocMarket after changes constitutes acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                11. Data Security Incidents
              </h2>
              <p className="text-gray-700">
                In the event of a data breach that affects your personal information, we will notify you within 72 hours via email and provide information about the incident, affected data, and steps you should take.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                12. Contact Us
              </h2>
              <p className="text-gray-700 mb-2">
                For questions, concerns, or to exercise your privacy rights:
              </p>
              <ul className="list-none text-gray-700 space-y-2 mt-2">
                <li><strong>Privacy inquiries:</strong> privacy@docmarket.com</li>
                <li><strong>Data deletion requests:</strong> privacy@docmarket.com</li>
                <li><strong>CCPA requests:</strong> privacy@docmarket.com</li>
                <li><strong>General support:</strong> support@docmarket.com</li>
              </ul>
              <p className="text-gray-700 mt-3">
                We will respond to all requests within 30 days.
              </p>
            </section>

            <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mt-6">
              <p className="text-blue-900 font-semibold mb-2">
                Summary of Key Privacy Points:
              </p>
              <ul className="list-disc pl-6 text-blue-800 space-y-1 text-sm">
                <li>We collect name, email, school, and content you upload</li>
                <li>Payment info is processed by Stripe - we don't store card details</li>
                <li>We only share data with Stripe for payment processing</li>
                <li>You can request data deletion at any time</li>
                <li>Must be 13 or older to use the platform</li>
                <li>Compliant with US laws including COPPA and CCPA</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
